// src/pages/InsightsDetail.jsx
import ReactDOM from "react-dom";
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { loadNewsData } from "../components/utils/storage";
import RelatedPosts from "../components/RelatedPosts";
import { CiCalendar, CiClock1 } from "react-icons/ci";
import bg1 from "../assets/images/life-bg.webp";
import NewsFormComp from "../components/NewsForm";
import SEO from "../components/SEO";

/* -----------------------------------------
   LANGUAGE PICKER
------------------------------------------ */
const pickLang = (value, lang) => {
  if (!value) return "";
  if (typeof value === "object") return value[lang] || value.en || "";
  return value;
};

const InsightsDetail = () => {
  /* GET PARAMS: /insights/:language/:id  */
  const { id, language } = useParams();
  const navigate = useNavigate();

  /* LOAD DATABASE */
  const db = loadNewsData();
  const langData = db[language] || { insights: [] };

  const insightsList = langData.insights;

  /* STATE */
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const isAdmin = localStorage.getItem("isAdmin") === "true";

  /* FIND INSIGHT */
  const post = insightsList.find((n) => n.id === Number(id)) || null;

  /* LOCK SCROLL ON MODAL */
  useEffect(() => {
    document.body.style.overflow = showModal ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [showModal]);

  /* IF NOT FOUND */
  if (!post) {
    return (
      <div className="container py-5 text-center">
        <h4>{language === "ar" ? "التحليل غير موجود" : "Insight not found"}</h4>
        <Link to={`/news/${language}`} className="btn btn-primary mt-3">
          {language === "ar" ? "رجوع" : "Back to Insights"}
        </Link>
      </div>
    );
  }

  /* PREPARE VALUES */
  const title = pickLang(post.title, language);
  const desc = pickLang(post.desc, language);
  const category = pickLang(post.category, language);

  const contentBlocks =
    typeof post.content === "object" && post.content[language]
      ? post.content[language]
      : post.content || [];

  const related = insightsList
    .filter(
      (item) =>
        pickLang(item.category, language) === category && item.id !== post.id
    )
    .slice(0, 3);

  const bg = post.img || bg1;

  const bannerStyle = {
     backgroundImage: `
    linear-gradient(
      rgba(0, 0, 0, 0.65),
      rgba(0, 0, 0, 0.65)
    ),
    url(${bg})
  `,
    backgroundSize: "cover",
    backgroundPosition: "center",
    height: "80vh",
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    color: "#111",
  };

  /* -----------------------------------------
      RENDER
  ------------------------------------------ */
  return (
    <div className="news-detail-page">
      <SEO
        title={`${title} | CaptainX Insights`}
        description={desc?.substring(0, 160) || "Insights from CaptainX Sports Technology"}
        path={`/insights/${language}/${id}`}
        language={language}
        type="article"
        image={post.img}
        article={{
          title: title,
          description: desc?.substring(0, 160),
          image: post.img,
          datePublished: post.date,
          dateModified: post.date
        }}
      />

      {/* HEADER */}
      <section className="tab py-4">
        <div className="tab-con">
          <div className="container">
            <div className="d-flex justify-content-between align-items-center mb-3 tab-con-text">
              <h6>{language === "ar" ? "الأخبار والتحليلات" : "News & Insights"}</h6>

              <div className="d-flex align-items-center gap-2">
                <button
                  className="explore-btn-border space-btn"
                  onClick={() => navigate(`/news?tab=news`)}
                >
                  {language === "ar" ? "الأخبار" : "News"}
                </button>

                <button
                  className="explore-btn  space-btn"
                  onClick={() => navigate(`/news?tab=insights`)}
                >
                  {language === "ar" ? "التحليلات" : "Insights"}
                </button>

                {isAdmin && (
                  <button
                    className="explore-btn-border space-btn"
                    onClick={() => {
                      setEditItem(null);
                      setShowModal(true);
                    }}
                  >
                    + {language === "ar" ? "إنشاء منشور" : "Create Post"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* MODAL */}
        {showModal &&
          ReactDOM.createPortal(
            <div
              className="custom-modal-overlay"
              onClick={() => setShowModal(false)}
            >
              <div
                className="custom-modal-box"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="custom-modal-header d-flex justify-content-between">
                  <h5 className="mb-0">
                    {editItem
                      ? language === "ar"
                        ? "تعديل المنشور"
                        : "Edit Post"
                      : language === "ar"
                      ? "إنشاء منشور"
                      : "Create Post"}
                  </h5>

                  <button className="btn-close" onClick={() => setShowModal(false)} />
                </div>

                <div className="custom-modal-body">
                  <NewsFormComp
                    language={language}
                    editItem={editItem}
                    setEditItem={setEditItem}
                    onAdd={() => {
                      setShowModal(false);
                      setEditItem(null);
                    }}
                  />
                </div>
              </div>
            </div>,
            document.body
          )}

        {/* BANNER */}
        <div className="container">
          <div className="banner-sec">
            <div style={bannerStyle} className="mb-4 position-relative banner-cons">
              <div className="banner-con">
                <h6 className="pageniation">{category}</h6>
                <h2 className="banner-text">{title}</h2>

                <p className="banner-time">
                  <CiCalendar className="me-1" />
                  {post.date}
                  <span className="me-3"></span>
                  <CiClock1 className="me-1" />
                  {post.time}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <div className="container pb-5">
        <div className="row justify-content-center">
          <div className="col-md-10">
            <p style={{ lineHeight: "1.8", fontWeight:"500"}}>{desc}</p>

            {/* Render content blocks */}
            {(() => {
              const textBlocks = contentBlocks.filter(b => b.type === "paragraph");
              const imageBlocks = contentBlocks.filter(b => b.type !== "paragraph");

              const firstFourTexts = textBlocks.slice(0, 4);
              const firstImage = imageBlocks[0];
              const oneMoreText = textBlocks[4] ? [textBlocks[4]] : [];

              const finalSequence = [
                ...firstFourTexts,
                ...(firstImage ? [firstImage] : []),
                ...oneMoreText
              ];

              return finalSequence.map((block, index) => (
                <div key={index} className="my-4">
                  {block.type === "paragraph" ? (
                    <p style={{ lineHeight: "1.8", fontSize: "1.15rem" }}>
                      {block.text}
                    </p>
                  ) : (
                    <img
                      src={block.src}
                      alt=""
                      className="img-fluid rounded"
                      style={{
                        width: "100%",
                        maxHeight: "450px",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* RELATED POSTS */}
      <div className="bg-light py-5">
        <div className="container">
          <h4 className="mb-4 text-center">
            {language === "ar" ? "منشورات متعلقة" : "Related Posts"}
          </h4>

          <RelatedPosts related={related} language={language} />
        </div>
      </div>
    </div>
  );
};

export default InsightsDetail;
